// Dungeon generation, ported from mapgen.c. Deterministic from the current RNG
// seed: 3000 rooms, BFS distance (RecurseSetDist), special rooms & checkpoints.

import { rand, rndnum, rndval } from './rng';
import { rooms, map, G } from './state';
import { RoomConnection } from './types';
import { getLoc } from '../render/assets';
import { LoadingScreen } from './screens';
import { MAP_W, MAP_H } from './constants';
import { applySavedMap } from '../net/save';

let rdir = 0;
let next_check = 30;
let DoRepeat = 0;

const floortiles = [12, 18, 19, 20];

export function Put(x: number, y: number, tile: number, room: number): void {
  map.m[map.w * y + x] = tile & 255;
  map.r[map.w * y + x] = room;
}

export function Get(x: number, y: number): number {
  if (x < 0) return 17;
  if (y < 0) return 17;
  if (x >= map.w) return 17;
  if (y >= map.h) return 17;
  return map.m[map.w * y + x];
}

export function GetRoom(x: number, y: number): number {
  if (x < 0) return -1;
  if (y < 0) return -1;
  if (x >= map.w) return -1;
  if (y >= map.h) return -1;
  return map.r[map.w * y + x];
}

export function GetVisited(x: number, y: number): number {
  if (x < 0) return 0;
  if (y < 0) return 0;
  if (x >= map.w) return 0;
  if (y >= map.h) return 0;
  // Void cells carry room id -1 (the C build read rooms[-1], harmless UB there);
  // treat "no room" as not-visited so void stays hidden.
  const r = GetRoom(x, y);
  if (r < 0) return 0;
  return rooms[r].visited;
}

function NewLevel(): void {
  map.w = MAP_W;
  map.h = MAP_H;
  for (let i = 0; i < map.w * map.h; i++) {
    map.m[i] = 17;
    map.r[i] = -1;
  }
}

export function DestroyDungeon(): void {
  for (let i = 0; i < G.total_rooms; i++) {
    rooms[i].con = null;
  }
  G.total_rooms = 0;
}

function ResetLevel(): void {
  map.w = MAP_W;
  map.h = MAP_H;
  G.total_rooms = 0;
  rdir = 0;
  next_check = 30;
  for (let i = 0; i < map.w * map.h; i++) {
    map.m[i] = 17;
    map.r[i] = -1;
  }
}

function CreateRoomDimensions(): { w: number; h: number } {
  let w = rndval(5, 12);
  let h = rndval(5, 12);
  if (w === 12) w = rndval(12, 15);
  if (h === 12) h = rndval(12, 15);
  return { w, h };
}

export function Paint(xp: number, yp: number, w: number, h: number, fname: string): void {
  const data = getLoc(fname);
  let k = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const b = k < data.length ? data[k] : 255; // fgetc EOF -> 255
      k++;
      Put(x + xp, y + yp, b, GetRoom(x + xp, y + yp));
    }
  }
}

function DrawRoom(
  place_x: number,
  place_y: number,
  room_w: number,
  room_h: number,
  room_id: number
): void {
  const f_type = rand() % 4;
  // Corners
  Put(place_x, place_y, 11, room_id);
  Put(place_x + room_w - 1, place_y, 10, room_id);
  Put(place_x, place_y + room_h - 1, 9, room_id);
  Put(place_x + room_w - 1, place_y + room_h - 1, 8, room_id);

  // Walls
  for (let i = 0; i < room_w - 2; i++) {
    Put(place_x + 1 + i, place_y + room_h - 1, 4, room_id);
    if (rand() % 16 === 0) Put(place_x + 1 + i, place_y + room_h - 1, 45 + (rand() % 2) * 4, room_id);
    Put(place_x + 1 + i, place_y, 5, room_id);
    if (rand() % 16 === 0) Put(place_x + 1 + i, place_y, 46 + (rand() % 2) * 4, room_id);
  }
  for (let i = 0; i < room_h - 2; i++) {
    Put(place_x + room_w - 1, place_y + 1 + i, 6, room_id);
    if (rand() % 16 === 0) Put(place_x + room_w - 1, place_y + 1 + i, 47 + (rand() % 2) * 4, room_id);
    Put(place_x, place_y + 1 + i, 7, room_id);
    if (rand() % 16 === 0) Put(place_x, place_y + 1 + i, 48 + (rand() % 2) * 4, room_id);
  }

  // Floor
  for (let y = 0; y < room_h - 2; y++) {
    for (let x = 0; x < room_w - 2; x++) {
      Put(place_x + 1 + x, place_y + 1 + y, floortiles[f_type], room_id);
    }
  }

  // Magic tiles
  if (room_id % 30 === 29) {
    if (Get(place_x + 1 + (rand() % (room_w - 2)), place_y + 1 + (rand() % (room_h - 2))) === floortiles[f_type]) {
      Put(place_x + 1 + (rand() % (room_w - 2)), place_y + 1 + (rand() % (room_h - 2)), 28 + (rand() % 3), room_id);
    }
  }
  // Save tiles
  if (room_id % 25 === 20) {
    const x = place_x + 1 + (rand() % (room_w - 2));
    const y = place_y + 1 + (rand() % (room_h - 2));
    if (Get(x, y) === floortiles[f_type]) Put(x, y, 31, room_id);
  }
  // Summon tiles
  if (room_id % 75 === 48) {
    const x = place_x + 1 + (rand() % (room_w - 2));
    const y = place_y + 1 + (rand() % (room_h - 2));
    if (Get(x, y) === floortiles[f_type]) Put(x, y, 32, room_id);
  }
  // Compass tile
  if (room_id % 20 === 19) {
    const x = place_x + 1 + (rand() % (room_w - 2));
    const y = place_y + 1 + (rand() % (room_h - 2));
    if (Get(x, y) === floortiles[f_type]) Put(x, y, 53, room_id);
  }

  if (room_id === 0) {
    Paint(place_x + 1, place_y + 1, room_w - 2, room_h - 2, 'dat/d/centre.loc');
  }
  if (room_id % 1000 === 499) {
    Paint(place_x + 1, place_y + 1, room_w - 2, room_h - 2, 'dat/d/weapon.loc');
  }
  if (room_id % 1000 === 999) {
    Paint(place_x + 1, place_y + 1, room_w - 2, room_h - 2, 'dat/d/bossroom.loc');
  }
}

function NoRoomCollision(place_x: number, place_y: number, room_w: number, room_h: number): number {
  if (place_x < 0) return 0;
  if (place_y < 0) return 0;
  if (place_x + room_w > map.w) return 0;
  if (place_y + room_h > map.h) return 0;
  for (let y = 0; y < room_h; y++) {
    for (let x = 0; x < room_w; x++) {
      if (Get(place_x + x, place_y + y) !== 17) return 0;
    }
  }
  return 1;
}

function MakeConnect(x: number, y: number, type: number): void {
  let nx = 0;
  let ny = 0;
  let d1 = 0;
  let d2 = 0;
  switch (type) {
    case 0:
      nx = x;
      ny = y - 1;
      d1 = 14;
      d2 = 13;
      break;
    case 1:
      nx = x;
      ny = y + 1;
      d1 = 13;
      d2 = 14;
      break;
    case 2:
      nx = x - 1;
      ny = y;
      d1 = 16;
      d2 = 15;
      break;
    case 3:
      nx = x + 1;
      ny = y;
      d1 = 15;
      d2 = 16;
      break;
  }

  const room_1 = GetRoom(x, y);
  const room_2 = GetRoom(nx, ny);
  if (room_1 % 1000 === 999) {
    d1 = d1 - 13 + 21;
    d2 = d2 - 13 + 38;
  } else if (room_2 % 1000 === 999) {
    d1 = d1 - 13 + 38;
    d2 = d2 - 13 + 21;
  }
  Put(x, y, d1, GetRoom(x, y));
  Put(nx, ny, d2, GetRoom(nx, ny));

  rooms[room_1].connections++;
  let rconnect: RoomConnection | null = rooms[room_1].con;
  rooms[room_1].con = { n: rconnect, x, y, x2: nx, y2: ny, c: room_2 };

  rooms[room_2].connections++;
  rconnect = rooms[room_2].con;
  rooms[room_2].con = { n: rconnect, x: nx, y: ny, x2: x, y2: y, c: room_1 };
}

function SuitableConnection(t: number): number {
  switch (t) {
    case 4:
    case 5:
    case 6:
    case 7:
    case 45:
    case 46:
    case 47:
    case 48:
    case 49:
    case 50:
    case 51:
    case 52:
      return 1;
  }
  return 0;
}

function NewRoom(
  place_x: number,
  place_y: number,
  room_w: number,
  room_h: number,
  creator: number
): void {
  const cplist_x: number[] = [];
  const cplist_y: number[] = [];
  const cplist_r: number[] = [];
  const cplist_t: number[] = [];
  const sr_cp: number[] = [];
  const sr_np: number[] = [];

  const id = G.total_rooms;
  rooms[id].checkpoint = 0;
  DrawRoom(place_x, place_y, room_w, room_h, id);

  rooms[id].x = place_x;
  rooms[id].y = place_y;
  rooms[id].w = room_w;
  rooms[id].h = room_h;
  rooms[id].room_type = 0;
  rooms[id].room_param = 0;
  rooms[id].creator = creator;
  rooms[id].connections = 0;
  rooms[id].con = null;
  rooms[id].enemies = 0;
  rooms[id].visited = 0;
  rooms[id].s_dist = -1;

  if (id === 0) rooms[id].checkpoint = 1;

  G.total_rooms++;

  if (creator === -1) return;

  let connect_points = 0;
  const push = (px: number, py: number, r: number, tt: number) => {
    cplist_x[connect_points] = px;
    cplist_y[connect_points] = py;
    cplist_r[connect_points] = r;
    cplist_t[connect_points] = tt;
    connect_points++;
  };

  for (let i = 0; i < room_w - 2; i++) {
    if (SuitableConnection(Get(place_x + 1 + i, place_y - 1))) {
      push(place_x + 1 + i, place_y, GetRoom(place_x + 1 + i, place_y - 1), 0);
    }
    if (SuitableConnection(Get(place_x + 1 + i, place_y + room_h))) {
      push(place_x + 1 + i, place_y + room_h - 1, GetRoom(place_x + 1 + i, place_y + room_h), 1);
    }
  }
  for (let i = 0; i < room_h - 2; i++) {
    if (SuitableConnection(Get(place_x - 1, place_y + 1 + i))) {
      push(place_x, place_y + 1 + i, GetRoom(place_x - 1, place_y + 1 + i), 2);
    }
    if (SuitableConnection(Get(place_x + room_w, place_y + 1 + i))) {
      push(place_x + room_w - 1, place_y + 1 + i, GetRoom(place_x + room_w, place_y + 1 + i), 3);
    }
  }

  let sr_cps = 0;
  let sr_nps = 0;
  for (let i = 0; i < connect_points; i++) {
    if (cplist_r[i] === creator) sr_cp[sr_cps++] = i;
    else sr_np[sr_nps++] = i;
  }

  if (sr_cps <= 0) return; // assert(sr_cps > 0) in original

  let i = rndval(0, sr_cps - 1);
  MakeConnect(cplist_x[sr_cp[i]], cplist_y[sr_cp[i]], cplist_t[sr_cp[i]]);

  if (sr_nps > 0) {
    i = rndval(0, sr_nps - 1);
    MakeConnect(cplist_x[sr_np[i]], cplist_y[sr_np[i]], cplist_t[sr_np[i]]);
  }
}

function AddChild(room_id: number): number {
  const r = rooms[room_id];
  const place_x = r.x;
  const place_y = r.y;
  const room_w = r.w;
  const room_h = r.h;
  let attempts = 0;

  for (;;) {
    attempts++;
    let new_w: number;
    let new_h: number;
    if ((G.total_rooms + 1) % 500 === 0) {
      new_w = 20;
      new_h = 15;
    } else {
      const d = CreateRoomDimensions();
      new_w = d.w;
      new_h = d.h;
    }

    const room_pos = rdir++ % 4;
    let new_x: number;
    let new_y: number;
    if (room_pos < 2) {
      new_x = rndval(place_x - (new_w - 3), place_x + (room_w - 3));
      new_y = room_pos === 0 ? place_y - new_h : place_y + room_h;
    } else {
      new_y = rndval(place_y - (new_h - 3), place_y + (room_h - 3));
      new_x = room_pos === 2 ? place_x - new_w : place_x + room_w;
    }

    if (NoRoomCollision(new_x, new_y, new_w, new_h)) {
      NewRoom(new_x, new_y, new_w, new_h, room_id);
      return 1;
    }
    if (attempts > 20) return 0;
  }
}

function RecurseSetDist(): void {
  const queue = new Int32Array(10000);
  let q_top = 1;
  let q_bot = 0;
  let rooms_left = 3000;
  queue[0] = 0;
  rooms[0].s_dist = 0;

  while (rooms_left > 0) {
    const c_room = queue[q_bot];
    q_bot++;
    rooms_left--;

    let rc = rooms[c_room].con;
    while (rc !== null) {
      if (rooms[rc.c].s_dist === -1) {
        queue[q_top] = rc.c;
        q_top++;
        rooms[rc.c].s_dist = rooms[c_room].s_dist + 1;
      }
      rc = rc.n;
    }
  }
}

function RoomSize(c_room: number): number {
  return Math.trunc(
    Math.sqrt(rooms[c_room].w * rooms[c_room].w + rooms[c_room].h * rooms[c_room].h)
  );
}

function MakeSpecialRooms(): void {
  const rtyp = [0, 0, 0, 0, 0, 0, 0, 0];

  for (let i = 0; i < 3; i++) {
    const c_room = i * 1000 + 999;
    rooms[c_room].room_type = 2;
    rooms[c_room].room_param = i;
  }
  for (let i = 0; i < 3; i++) {
    const c_room = i * 1000 + 499;
    rooms[c_room].room_type = 5;
    rooms[c_room].room_param = i;
  }

  for (let c_tier = 0; c_tier < 8; c_tier++) {
    let biggest_room_sz = 0;
    let biggest_room_n = -1;
    for (let c_room = 0; c_room < 3000; c_room++) {
      if (rooms[c_room].room_type === 0) {
        if (rooms[c_room].s_dist >= c_tier * 5 + 5) {
          if (rooms[c_room].s_dist <= c_tier * 5 + 9) {
            if (RoomSize(c_room) > biggest_room_sz) {
              biggest_room_sz = RoomSize(c_room);
              biggest_room_n = c_room;
            }
          }
        }
      }
    }
    if (biggest_room_n >= 0) {
      rooms[biggest_room_n].room_type = 3;
      for (;;) {
        const ctyp = rand() % 8;
        if (rtyp[ctyp] === 0) {
          rtyp[ctyp] = 1;
          rooms[biggest_room_n].room_param = ctyp;
          break;
        }
      }
    }
  }

  // place of power: highest s_dist plain room
  for (let i = 0; i < 3000; i++) {
    if (rooms[i].s_dist > rooms[G.place_of_power].s_dist) {
      if (rooms[i].room_type === 0) {
        G.place_of_power = i;
      }
    }
  }
  rooms[G.place_of_power].room_type = 6;

  // checkpoints: one per 64x64 grid cell
  for (let y = 0; y < 8; y++) {
    for (let x = 0; x < 8; x++) {
      for (let i = 0; i < 20; i++) {
        const j = GetRoom((rand() % 64) + x * 64, (rand() % 64) + y * 64);
        if (j >= 0) {
          if (rooms[j].room_type === 0) {
            Put(rooms[j].x + Math.trunc(rooms[j].w / 2), rooms[j].y + Math.trunc(rooms[j].h / 2), 25, j);
            rooms[j].checkpoint = 1;
            break;
          }
        }
      }
    }
  }

  next_check--;
}

function Generate(): number {
  rdir = rand() % 4;
  NewRoom(
    Math.trunc(map.w / 2) - Math.trunc(20 / 2),
    Math.trunc(map.h / 2) - Math.trunc(15 / 2),
    20,
    15,
    -1
  );

  for (let attempts = 0; attempts < 100000; attempts++) {
    AddChild(rndval(rndval(0, G.total_rooms - 1), G.total_rooms - 1));
    if (G.total_rooms % 100 === 99) {
      LoadingScreen(0, G.total_rooms / 3000.0);
    }
    if (G.total_rooms === 3000) break;
  }

  if (G.total_rooms < 3000 || DoRepeat === 1) {
    DoRepeat = 0;
    ResetLevel();
    return 0;
  }

  RecurseSetDist();

  let correct_dist = 0;
  for (let i = 0; i < 3000; i++) {
    if (rooms[i].s_dist >= 50) correct_dist = 1;
  }

  if (correct_dist === 0) {
    DoRepeat = 0;
    ResetLevel();
    return 0;
  }

  MakeSpecialRooms();
  map.totalRooms = G.total_rooms;
  return 1;
}

export function RandomGenerateMap(): void {
  G.place_of_power = 0;
  rdir = 0;
  next_check = 30;
  DoRepeat = 0;
  NewLevel();
  if (G.game_load) {
    applySavedMap();
    return;
  }
  let trying = 1;
  while (trying) {
    trying = Generate() ? 0 : 1;
  }
}
